import { entityEffects, ICurrentEntity, selectCurrentEntity, selectEntityDisplayName } from '@opengov/app-core/entity';
import { getEntity, getEntitySubdomain, getNumericalEntityId } from '@opengov/app-core/entity/selectors';
import { coreFeatureFlagEffects } from '@opengov/app-core/featureFlag';
import { userSelector } from '@opengov/app-core/session/selectors';
import { ensureAll } from '@opengov/compose-app-router/onEnter';
import { getLocation } from '@opengov/compose-app-router/selectors';
import { routerActions } from '@opengov/compose-app-router/state';
import { generatePageTitle } from '@opengov/stories-shared/utils';

import { IPublicationSetupPdfProps, PublicationSetupPdf } from '../components/Publications/SetupPDF';
import { onEnterStoriesRoutes } from '../../routes/onEnter';
import { maintenancePath, storyListPath } from '../../routes/paths';
import { navigateToPublicationView } from '../state/navigation';
import { storiesMsgs } from '../../locale';

type NextLocationParams<Params> = {
  params: Params;
};

type OnEnterCallback = () => void;
type OnErrorCallback = (message: string, action: unknown) => void;

interface IPublicationSetupPdfRouteParams {
  entitySubdomain: string;
  exportId: string;
  exportType: string;
}

type setupPdfNextLocation = NextLocationParams<IPublicationSetupPdfRouteParams>;

// `state: any` is required by the IRoute interface from @opengov/compose-app-router
// (`selector?(state: any): any`). The library never exported a typed root state —
// all selectors it ships accept `any`. This goes away when compose-app-router is
// replaced by React Router v7 loaders in the router-migration stories.
const setupPdfSelector = (state: any): IPublicationSetupPdfProps => {
  const location = getLocation(state);
  const { exportType, exportId, entitySubdomain: urlEntitySubdomain } = (location.params || {}) as IPublicationSetupPdfRouteParams;
  const storeEntitySubdomain = getEntitySubdomain(state);
  // On page refresh the Redux store briefly holds the previous entity before
  // fetchRequestedOrCurrentEntity completes. Guard against that by returning
  // entityId=0 whenever the store subdomain doesn't yet match the URL subdomain,
  // so any query that uses entityId stays disabled until the store is current.
  const entityId = urlEntitySubdomain && storeEntitySubdomain === urlEntitySubdomain
    ? getNumericalEntityId(state)
    : 0;
  return {
    entityId,
    entityName: getEntity(state).display_name,
    entitySubdomain: storeEntitySubdomain,
    messages: { ...storiesMsgs(state).pdfSetup },
    navigateToPublicationView: (entitySubdomain, publicationId) =>
      navigateToPublicationView(entitySubdomain, publicationId),
    publicationId: exportType === 'publications' ? (exportId || '') : '',
    sectionId: exportType === 'sections' ? (exportId || '') : '',
    user: userSelector(state)
  };
};

export const setupPdfRoute = {
  name: 'PublicationSetupPdf',
  // `state: any` — ITitleFunction in @opengov/compose-app-router types it as `(state: any) => string`.
  title: (state: any) => generatePageTitle('Setup PDF', selectEntityDisplayName(state)),
  selector: setupPdfSelector,
  component: PublicationSetupPdf,
  onEnter: ensureAll([
    onEnterStoriesRoutes,
    async (state, nextLocation: setupPdfNextLocation, onSuccess: OnEnterCallback, onError: OnErrorCallback) => {
      const { entitySubdomain, exportId, exportType: _exportType } = nextLocation.params;
      await entityEffects
        .fetchRequestedOrCurrentEntity({
          requestedEntitySubdomain: entitySubdomain,
          currentEntity: selectCurrentEntity(state)
        })
        .then(async (entity: ICurrentEntity) => {
          if (entity.subdomain !== entitySubdomain) {
            return onError(
              'Entity not found...',
              routerActions.replace.create({ pathname: storyListPath(entity.subdomain) })
            );
          }
          await coreFeatureFlagEffects.getFlags().then(async (flags) => {
            if (flags && !flags['product-feature-publications-setup-pdf']) {
              navigateToPublicationView(entitySubdomain, exportId);
            }

            if (flags && flags['product-stories-maintenance-window']) {
              return onError(
                'Redirecting to maintenance page...',
                routerActions.replace.create({ pathname: maintenancePath(entitySubdomain) })
              );
            }
            onSuccess();
          });
        });
    }
  ])
};
